import React, { useState } from 'react';
import * as _ from 'lodash';
import classNames from 'classnames';
import styles from '../styles/wizards/LinkExistingFilesWizard.scss';
import { __ } from '../core/utils';
import { setNotification } from '../redux/actions/general/notification';
import CloseIcon from '../assets/images/close-big-15x15.svg';
import ArrowRightMiddleIcon from '../assets/images/arrow-right-middle-15x15.svg';
import ArrowLeftMiddleIcon from '../assets/images/arrow-left-middle-15x15.svg';
import ArrowDoubleRightMiddleIcon from '../assets/images/arrow-double-right-middle-15x15.svg';
import ArrowDoubleLeftMiddleIcon from '../assets/images/arrow-double-left-middle-15x15.svg';
import Button from '../components/Button';
import ButtonGroup from '../components/ButtonGroup';
import FilesBrowser from './link_existing_files/FilesBrowser';
import useFilesBrowser from './link_existing_files/useFilesBrowser';

const LinkExistingFilesWizard = ({
  stage_id,
  linked_file_ids,
  onLinkFiles,
  onClose,
  showSuccessNotification,
  title,
}) => {
  const browser = useFilesBrowser(stage_id, linked_file_ids);
  const [linking, setLinking] = useState(false);
  const visiblePagesLimit = 5;
  const totalPages = parseInt(browser.pages);
  const currentPage = parseInt(browser.page);
  let startPage;
  let endPage;

  if (totalPages <= visiblePagesLimit) {
    startPage = 1;
    endPage = totalPages;
  } else if (currentPage <= visiblePagesLimit - 1) {
    startPage = 1;
    endPage = visiblePagesLimit;
  } else if (currentPage + 2 >= totalPages) {
    startPage = totalPages - visiblePagesLimit + 1;
    endPage = totalPages;
  } else {
    startPage = currentPage - 3;
    endPage = currentPage + 2;
  }

  const linkSelected = () => {
    if (_.isEmpty(browser.selectedFileIds) || !onLinkFiles) {
      return;
    }

    setLinking(true);

    Promise.resolve()
      .then(() => onLinkFiles(browser.selectedFileIds, browser.selectedFiles))
      .then(() => {
        if (showSuccessNotification) {
          setNotification(
            browser.selectedFileIds.length === 1
              ? __('files.wizard.file-linked', {}, 'File has been linked.')
              : __('files.wizard.files-linked', {}, 'Files have been linked.'),
          );
        }
        onClose();
      })
      .catch(() => setLinking(false));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span>
          {title ||
            __('files.wizard.link-existing-file', {}, 'Link existing file')}
        </span>
        <CloseIcon onClick={onClose} />
      </div>

      <FilesBrowser browser={browser} />

      <div className={styles.footer}>
        <div className={styles.pagination}>
          <button
            className={styles.paginationArrow}
            disabled={browser.page <= 1}
            onClick={() => browser.setPage(1)}
          >
            <ArrowDoubleLeftMiddleIcon />
          </button>
          <button
            className={styles.paginationArrow}
            disabled={browser.page <= 1}
            onClick={() => browser.setPage(browser.page - 1)}
          >
            <ArrowLeftMiddleIcon />
          </button>
          {_.map(_.range(startPage, endPage + 1), (page) => (
            <span
              onClick={() => browser.setPage(page)}
              key={page}
              className={classNames(
                styles.page,
                browser.page == page && styles.active,
              )}
            >
              {_.padStart(page, 2, '0')}
            </span>
          ))}
          <button
            className={styles.paginationArrow}
            disabled={browser.page >= browser.pages}
            onClick={() => browser.setPage(browser.page + 1)}
          >
            <ArrowRightMiddleIcon />
          </button>
          <button
            className={styles.paginationArrow}
            disabled={browser.page >= browser.pages}
            onClick={() => browser.setPage(totalPages)}
          >
            <ArrowDoubleRightMiddleIcon />
          </button>
        </div>
        <div className={styles.actions}>
          <Button
            lightGray
            medium
            left
            middleText={__('button.cancel')}
            onClick={onClose}
          />
          <span className={styles.selectionCount}>
            {_.isEmpty(browser.selectedFileIds)
              ? __('files.wizard.no-files-selected', {}, 'No files selected')
              : browser.selectedFileIds.length +
                ' ' +
                __('files.wizard.files-selected', {}, 'files selected')}
          </span>

          <ButtonGroup>
            <Button
              lightBlue
              medium
              className={styles.linkButton}
              middleText={__('files.wizard.link-selected', {}, 'Link selected')}
              spinning={linking}
              disabled={
                _.isEmpty(browser.selectedFileIds) || linking || !onLinkFiles
              }
              onClick={linkSelected}
            />
          </ButtonGroup>
        </div>
      </div>
    </div>
  );
};

LinkExistingFilesWizard.defaultProps = {
  linked_file_ids: [],
  onLinkFiles: null,
  showSuccessNotification: true,
  title: null,
};

export default LinkExistingFilesWizard;
